import type { Drawing } from "@mnk/shared/models/drawing";
import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import type { Project } from "@mnk/shared/models/project";
import { memo, useEffect, useMemo, useState } from "react";
import { DrawingArea } from "../drawingArea";
import { useImageToPdf } from "./useImageToPdf";

interface Props {
  zoom: number;
  project: Project;
  drawing: Drawing;
  totalCount: number;
  drawingImages: DrawingImage[];
  quantityId: string;
  officeName: string;
  showFlag: boolean;
  setShowDownloader: (showDownloader: boolean) => void;
}

export const Downloader = memo((props: Props) => {
  const [downloading, setDownloading] = useState(false);
  const [convertProg, setConvertProg] = useState({ done: 0, total: 0 }); // 追加
  const [currentIndex, setCurrentIndex] = useState(0);
  const { project, totalCount, setShowDownloader, drawingImages, zoom, quantityId, officeName, showFlag, drawing } =
    props;
  const [dataURLs, setDataURLs] = useState<Record<number, string>>({});
  const { convert } = useImageToPdf();
  // 処理中フラグを追加して重複実行を防ぐ
  const [isProcessing, setIsProcessing] = useState(false);

  const currentDrawingImage = useMemo(() => {
    return drawingImages[currentIndex];
  }, [drawingImages, currentIndex]);

  const completedCount = useMemo(() => {
    return Object.keys(dataURLs).length;
  }, [dataURLs]);

  const renderingCompleted = useMemo(() => {
    return completedCount === totalCount;
  }, [completedCount, totalCount]);

  // saveImageにindexを引数として渡すように変更
  const saveImage = (dataURL: string, index: number) => {
    // 既に保存済みの場合はスキップ
    setDataURLs((prev) => {
      if (prev[index] !== undefined) {
        return prev;
      }
      return { ...prev, [index]: dataURL };
    });
  };

  // currentIndexの更新をuseEffectで行う（dataURLsの変更を検知）
  useEffect(() => {
    if (isProcessing) return;
    // 次の未処理のインデックスを探す
    const nextIndex = Object.keys(dataURLs).length;
    if (nextIndex < totalCount && nextIndex !== currentIndex) {
      setCurrentIndex(nextIndex);
    }
  }, [dataURLs, totalCount, currentIndex, isProcessing]);

  useEffect(() => {
    console.log("dataURLs", Object.keys(dataURLs).length);
    console.log("convertProg", convertProg);
  }, [dataURLs, convertProg]);

  const handleDownload = async () => {
    setDownloading(true);
    setIsProcessing(true);
    try {
      console.log("Starting PDF conversion..."); // デバッグログ追加
      // dataURLsをインデックス順にソートして配列に変換
      const sortedDataURLs = Object.entries(dataURLs)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, url]) => url);
      await convert(sortedDataURLs, project.name, (done, total) => setConvertProg({ done, total }));
      // ★ convert が完了したあとに後処理を行う
      setDataURLs({});
      setShowDownloader(false);
      console.log("PDF conversion completed"); // デバッグログ追加
      // 完了後の後処理
    } finally {
      setDownloading(false);
      setIsProcessing(false);
      setConvertProg({ done: 0, total: 0 });
    }
  };

  return (
    <div className="fixed bottom-4 left-4 bg-white p-6 rounded-lg shadow-lg max-w-md w-full border border-gray-300">
      <div className="hidden">
        <DrawingArea
          autoCapture={!renderingCompleted}
          saveImage={saveImage}
          currentIndex={currentIndex}
          currentDrawingImage={currentDrawingImage}
          zoom={zoom}
          project={project}
          drawing={drawing}
          totalCount={totalCount}
          quantityId={quantityId}
          officeName={officeName}
          showFlag={showFlag}
        />
      </div>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">PDF生成中</h3>
        <div className="space-y-2">
          <div className="w-full bg-gray-100 rounded-full h-3">
            <div
              className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>進捗状況</span>
            <span>
              {completedCount} / {totalCount}
            </span>
          </div>
        </div>
        {renderingCompleted && (
          <div className="pt-4">
            <button
              type="button"
              onClick={handleDownload}
              className={`w-full bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer ${
                downloading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700 cursor-pointer"
              }`}
              disabled={downloading}
            >
              {downloading ? (
                <div className="w-5 h-5 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <title>PDFをダウンロード</title>
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span>{downloading ? "ダウンロード中..." : "PDFをダウンロード"}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
